import { useEffect, useRef, useState } from 'react';
import { useApi } from '@backstage/core-plugin-api';
import { useTemplateSecrets } from '@backstage/plugin-scaffolder-react';
import { microsoftAzUserAuthApiRef } from '../../modules/microsoftAzUserAuth';

const SECRET_NAME = 'AZURE_AZ_USER_TOKEN';
const ARM_USER_IMPERSONATION_SCOPE = 'https://management.azure.com/user_impersonation';

export type SignInStatus = 'idle' | 'gettingToken' | 'signedIn' | 'error';

export type SignedInUser = {
  name: string;
  username?: string;
};

type AzTokenClaims = {
  name?: string;
  preferred_username?: string;
  upn?: string;
  unique_name?: string;
  email?: string;
};

const decodeTokenClaims = (token: string): AzTokenClaims | undefined => {
  try {
    const payload = token.split('.')[1];
    if (!payload) {
      return undefined;
    }
    // base64url → base64, then decode as UTF-8 so non-ASCII names survive.
    const base64 = payload.replace(/-/g, '+').replace(/_/g, '/');
    const json = decodeURIComponent(
      atob(base64)
        .split('')
        .map(char => `%${char.charCodeAt(0).toString(16).padStart(2, '0')}`)
        .join(''),
    );
    return JSON.parse(json) as AzTokenClaims;
  } catch {
    return undefined;
  }
};

const getSignedInUser = (token: string): SignedInUser => {
  const claims = decodeTokenClaims(token);
  const username = claims?.upn ?? claims?.unique_name;
  const name = claims?.name ?? username ?? 'your Azure account';
  return { name, username };
};

// Automatically runs sign-out and token acquisition once on mount.
export const useAzureSignIn = (): {
  status: SignInStatus;
  user?: SignedInUser;
} => {
  const auth = useApi(microsoftAzUserAuthApiRef);
  const { setSecrets } = useTemplateSecrets();
  const [status, setStatus] = useState<SignInStatus>('idle');
  const [user, setUser] = useState<SignedInUser>();
  const isActiveRef = useRef(false);

  useEffect(() => {
    if (isActiveRef.current) {
      return () => undefined;
    }
    isActiveRef.current = true;

    setSecrets({ [SECRET_NAME]: '' });
    setUser(undefined);
    setStatus('idle');

    const getAccessToken = async () => {
      setStatus('gettingToken');
      try {
        await auth.signOut();
        const token = await auth.getAccessToken([ARM_USER_IMPERSONATION_SCOPE], {
          instantPopup: true,
        });

        if (!isActiveRef.current) return;

        if (!token) {
          setSecrets({ [SECRET_NAME]: '' });
          setUser(undefined);
          setStatus('error');
          return;
        }

        setSecrets({ [SECRET_NAME]: token });
        setUser(getSignedInUser(token));
        setStatus('signedIn');
      } catch {
        if (!isActiveRef.current) return;

        setSecrets({ [SECRET_NAME]: '' });
        setUser(undefined);
        setStatus('error');
      }
    };

    getAccessToken();

    return () => {
      isActiveRef.current = false;
      void auth.signOut();
    };
  }, [auth, setSecrets]);

  return { status, user };
};
